import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  JsonInput,
  Loader,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  type TextInputProps,
  Title,
} from '@mantine/core';
import { DateInput, DateTimePicker } from '@mantine/dates';
import { type UseFormReturnType, useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DataTable,
  type DataTablePaginationProps,
  type DataTableSortProps,
  type DataTableSortStatus,
} from 'mantine-datatable';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { rawApi } from '../api/client';
import { type ApiError, fieldErrors } from '../api/errors';
import { listKey } from '../api/queryKeys';
import type { EntityKey } from '../lib/entities';
import { type EnumGroup, enumColor, enumLabel, enumOptions } from '../lib/enums';
import { formatDate, formatDateTime, formatMoney, formatRateBp, fromIsoDate, toIsoDate } from '../lib/format';
import { notifyError, notifySaved, notifyWarnings } from '../lib/notify';
import { isValidInternationalPhone } from '../lib/phone';
import { withArticle } from '../lib/text';
import { EligibilityField } from './EligibilityField';
import { EntityLabel, EntityMultiSelect, EntitySelect } from './EntityField';
import { PhoneField } from './PhoneField';
import { ScheduleField } from './ScheduleField';
import { WeekdaysField } from './WeekdaysField';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from './WritingIntoAFiledMonth';

/**
 * The generic table-and-form screen behind most of this portal.
 *
 * Its job is the genuinely repetitive case: a flat collection where a row is a handful of fields.
 * It is deliberately NOT the answer for a screen with a subject and related history — those get
 * a purpose-built page on top of `RecordScreen`, because bending a config-driven component into
 * one produces something nobody can safely change later. Keeping that line clear is what stops
 * this file from turning into a framework.
 *
 * Everything user-facing that used to be per-page and slightly different — money and date
 * formatting, enum wording, error copy, cache keys — now comes from shared modules, so a fix
 * lands on all fifteen pages at once instead of fifteen times.
 */

/**
 * How a numeric field is stored versus shown. Money is integer minor units, rates are integer
 * basis points, FX is integer micro-units; all three are chosen so arithmetic stays exact, and
 * all three are unreadable raw. `unit` may be a function because several forms switch meaning on
 * a sibling field (a statutory parameter is a percentage or an amount depending on its kind).
 */
export type FieldUnit = 'money' | 'percent' | 'micro' | 'plain';

export interface FieldDef {
  name: string;
  label: string;
  type:
    | 'text'
    | 'phone'
    | 'number'
    | 'boolean'
    | 'select'
    | 'json'
    | 'date'
    | 'datetime'
    | 'entity'
    | 'entities'
    | 'rows'
    | 'schedule'
    | 'weekdays'
    | 'eligibility';
  /** Explanatory line under the input. Use this instead of stuffing examples into the label. */
  description?: string;
  placeholder?: string;
  options?: { value: string; label: string; description?: string }[];
  /** Draws options and wording from the shared enum registry. */
  enumGroup?: EnumGroup;
  /** For `type: 'entity'` — which collection to pick from; a function switches on sibling values. */
  resource?: EntityKey | ((values: Record<string, unknown>) => EntityKey | undefined);
  required?: boolean;
  /** Value a new record starts with. Mirror the column's database default, especially for flags. */
  defaultValue?: unknown;
  unit?: FieldUnit | ((values: Record<string, unknown>) => FieldUnit);
  /** Column is nullable, so clearing the box must send an explicit null rather than be dropped. */
  nullable?: boolean;
  /**
   * Settable when the record is created, and refused by the update endpoint afterwards.
   *
   * Several of these endpoints deliberately freeze part of a record — a CAI and its number range,
   * a reason code the stock ledger has already written against, the currency an account holds.
   * The update body simply does not accept those fields, and an unknown key is dropped rather
   * than refused, so editing one used to produce a green "Saved" over a change that never
   * happened. Shown, greyed, with the reason: hiding it would leave the reader wondering where
   * the CAI went, and letting them type into it is the lie.
   */
  fixedAfterCreate?: boolean;
  /** Extra validation, run before the request. Return a message to block submission. */
  validate?: (value: unknown, values: Record<string, unknown>) => string | null;
  /**
   * What this box fills itself in with, from what has been typed into the others.
   *
   * For `type: 'text'` only, and for one question: what more than one of a unit is called. The
   * plural of a free-text unit cannot be inferred at print time — "2 kgs" is what that rule
   * produces — so the platform asks, and asking must not mean interrogating somebody adding a
   * stock item at seven in the evening. The box therefore arrives holding the best answer going:
   * what the business has already said about that word, or a plain suggestion where nobody has
   * said anything yet.
   *
   * **The suggestion is shown, not applied.** It sits in the box in black text where it can be
   * read and typed over, and it reaches the server only because the owner saved the form he was
   * looking at. Nothing is ever inferred behind him — a word with no answer on file still prints
   * its singular everywhere, for ever, which is what `lib/units.ts` guarantees.
   *
   * Following stops the moment the box is typed in, so a corrected answer is never overwritten by
   * a later change to the field it was derived from.
   */
  suggest?: (values: Record<string, unknown>) => string;
  /**
   * For `type: 'rows'` — the shape of one entry in a repeating list.
   *
   * This exists because the alternative was asking the owner to hand-encode storage units inside a
   * JSON box: 15% typed as `1500`, L 5,000 typed as `500000`. Every scalar field in this component
   * already converts units for him; a value one level inside a list is not a different kind of
   * number, and a dropped zero there sets the tax on every item in a group.
   */
  rowFields?: FieldDef[];
  /**
   * The BARE singular noun for one row: `charge`, `tier`. The article belongs to whichever sentence
   * uses it — "Add a charge" on the button, "Add at least one charge" when the list is empty — and
   * a noun carrying its own "a" produced "Add at least one a tier" on four screens.
   */
  rowNoun?: string;
}

export interface ColumnDef {
  accessor: string;
  title?: string;
  /** Lets a purpose-built server page opt a column into the table library's sort control. */
  sortable?: boolean;
  render?: (row: Record<string, unknown>) => ReactNode;
  /** Resolve this stored id to the record's name. */
  reference?: EntityKey;
  /** Label (and colour, where the group defines one) from the shared enum registry. */
  enumGroup?: EnumGroup;
  /** Format a stored numeric/date value for reading. */
  unit?: FieldUnit | 'date' | 'datetime';
  /** Currency accessor for money columns; falls back to the row's `currency`. */
  currencyAccessor?: string;
  noWrap?: boolean;
}

export interface CrudSectionProps {
  /** Collection name, used as the table heading. */
  title: string;
  /**
   * BARE singular noun, used in buttons and dialogs: `staff member` → "Add a staff member". Bare
   * because the article is chosen from the word — `answer` reads "Add an answer", and did not.
   */
  entityNoun?: string;
  description?: string;
  listPath: string;
  /** Wait for required page context (normally location) before asking for the list. */
  listEnabled?: boolean;
  /** Query string parameters, kept out of `listPath` so the cache key stays honest. */
  listQuery?: Record<string, string | number | undefined>;
  createPath?: string;
  /** `{id}` replaced with the row id; enables editing. */
  updatePathTemplate?: string;
  columns: ColumnDef[];
  fields?: FieldDef[];
  /** Hidden values merged into every create body (locationId etc.). */
  defaults?: Record<string, unknown>;
  extractList?: (data: unknown) => Record<string, unknown>[];
  /** How to name one row in dialogs and confirmations. */
  rowLabel?: (row: Record<string, unknown>) => string;
  actions?: ReactNode;
  /** Search and filter controls that belong to the list, below the add/edit heading. */
  tableControls?: ReactNode;
  /** Controlled server-side pagination; total is read from the endpoint's page envelope. */
  pagination?: {
    page: number;
    recordsPerPage: number;
    totalRecords: (data: unknown) => number;
    onPageChange: (page: number) => void;
  };
  /** Controlled server-side sorting. Columns opt in with `sortable`. */
  sortStatus?: DataTableSortStatus<Record<string, unknown>>;
  onSortStatusChange?: (status: DataTableSortStatus<Record<string, unknown>>) => void;
  rowActions?: (row: Record<string, unknown>, refresh: () => void) => ReactNode;
  /**
   * Tint a row that needs attention, so the table and the banner above it agree.
   *
   * Deliberately a colour and not a status column: the row already says what it is in words, and
   * a second column repeating "warning" is noise. This is for the case where a banner is telling
   * the owner that one of these rows is the problem and the table gives no clue which.
   */
  rowBackgroundColor?: (row: Record<string, unknown>) => string | undefined;
  /**
   * The record the create call returned, for a page where adding one is not the end of the job.
   *
   * A menu item is the case this exists for: adding one leaves something that cannot be rung up
   * until it is priced, and the row that was just created is the row the next step is about. Left
   * to the page rather than done here, because "what happens next" is a judgement about the
   * subject, and most collections genuinely end at Add.
   */
  onCreated?: (created: Record<string, unknown>) => void;
  /** Render only the create button — for the few sections that have no meaningful list. */
  formOnly?: boolean;
  /** Shown when the collection is genuinely empty; say what to do, not just that it is empty. */
  emptyMessage?: string;
}

/** Storage → display for every field of every row, so a rate arrives as 15 rather than 1500. */
function rowsToDisplay(value: unknown, rowFields: FieldDef[]): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.map((row) => {
    const source = (row ?? {}) as Record<string, unknown>;
    const out: Record<string, unknown> = { ...source };
    for (const f of rowFields) {
      if (f.type === 'number') out[f.name] = toDisplayNumber(source[f.name], resolveUnit(f.unit, source));
    }
    return out;
  });
}

/**
 * Display → storage, dropping rows the user left blank.
 *
 * "Blank" means nothing was TYPED — a field still sitting on its default does not count. Otherwise a
 * row whose currency and method are pre-picked looks filled the moment it is added, so clicking
 * "Add a minimum" and changing your mind posts a threshold with no amount, and the save fails on a
 * row the user thought they had abandoned.
 */
function rowsToStored(value: unknown, rowFields: FieldDef[]): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row) =>
      rowFields.some((f) => {
        const v = (row as Record<string, unknown>)?.[f.name];
        if (v === '' || v === null || v === undefined) return false;
        return f.defaultValue === undefined || v !== f.defaultValue;
      }),
    )
    .map((row) => {
      const source = (row ?? {}) as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const f of rowFields) {
        const v = source[f.name];
        if (v === '' || v === null || v === undefined) continue;
        out[f.name] = f.type === 'number' ? toStoredNumber(v, resolveUnit(f.unit, source)) : v;
      }
      return out;
    });
}

function resolveUnit(unit: FieldDef['unit'], values: Record<string, unknown>): FieldUnit {
  if (!unit) return 'plain';
  return typeof unit === 'function' ? unit(values) : unit;
}

/** Stored integer → the number a person edits. */
function toDisplayNumber(stored: unknown, unit: FieldUnit): number | '' {
  if (typeof stored !== 'number' || !Number.isFinite(stored)) return '';
  switch (unit) {
    case 'money':
      return stored / 100;
    case 'percent':
      return stored / 100;
    case 'micro':
      return stored / 1_000_000;
    default:
      return stored;
  }
}

/** Edited number → the integer that goes over the wire. */
function toStoredNumber(entered: unknown, unit: FieldUnit): number | undefined {
  const n = typeof entered === 'string' ? Number(entered) : (entered as number);
  if (!Number.isFinite(n)) return undefined;
  switch (unit) {
    case 'money':
      return Math.round(n * 100);
    case 'percent':
      return Math.round(n * 100);
    case 'micro':
      return Math.round(n * 1_000_000);
    default:
      return n;
  }
}

const UNIT_DECIMALS: Record<FieldUnit, number | undefined> = {
  money: 2,
  percent: 2,
  // FX is stored against a 1e6 scale; copying money's 2 decimals here would silently truncate a
  // rate like 26.712345 down to 26.71 on every edit.
  micro: 6,
  plain: undefined,
};

/** Every form here is untransformed and loosely keyed; the alias saves restating that everywhere. */
export type CrudForm = UseFormReturnType<Record<string, unknown>>;

/**
 * `FormFields`, `buildBody` and `seedFormValues` are exported for RECORD SCREENS, and nothing else.
 *
 * A record screen owns its subject — the staff page is the precedent and the reason: while a person
 * could be edited both from the list and from their own page, the same phone number had two homes
 * and no way to tell which one a manager had used. Moving that editing onto the subject's own page
 * should not mean writing its twenty fields out a second time in a different shape, because two
 * declarations of the same form drift apart from the day the second one is written — one of them
 * quietly stops converting a rate out of basis points, and nobody finds out until a commission is
 * a hundred times wrong. So the declarations stay in one place and the rendering, the unit
 * conversion and the empty-versus-null rules are shared rather than reimplemented.
 *
 * This is deliberately not a general form framework and must not become one: it is three functions
 * over the same `FieldDef[]` this file already owns.
 */
export function FormFields({
  form,
  fields,
  editing,
}: {
  form: CrudForm;
  fields: FieldDef[];
  editing: boolean;
}) {
  const values = form.getValues();
  return (
    <Stack gap="md">
      {fields.map((f) => {
        const frozen = editing && Boolean(f.fixedAfterCreate);
        const common = {
          label: f.label,
          description: frozen ? (f.description ?? 'Fixed once saved.') : f.description,
          required: f.required,
          error: form.errors[f.name],
          disabled: frozen,
        };

        switch (f.type) {
          case 'phone':
            return (
              <PhoneField
                key={f.name}
                label={f.label}
                description={f.description}
                required={f.required}
                disabled={frozen}
                error={form.errors[f.name]}
                value={String(values[f.name] ?? '')}
                onChange={(value) => form.setFieldValue(f.name, value)}
                name={f.name}
              />
            );
          case 'number': {
            const unit = resolveUnit(f.unit, values);
            return (
              <NumberInput
                key={f.name}
                {...common}
                {...form.getInputProps(f.name)}
                decimalScale={UNIT_DECIMALS[unit]}
                // Never round the user's typing away silently; only fix the scale on blur.
                fixedDecimalScale={unit === 'money'}
                thousandSeparator={unit === 'money' ? ',' : undefined}
                suffix={unit === 'percent' ? '%' : undefined}
                placeholder={f.placeholder}
                hideControls={unit === 'money'}
              />
            );
          }
          case 'boolean':
            return (
              <Checkbox
                key={f.name}
                label={f.label}
                description={f.description}
                disabled={frozen}
                {...form.getInputProps(f.name, { type: 'checkbox' })}
              />
            );
          case 'select': {
            // An explicit `options` list wins, since several forms deliberately offer a subset of
            // a group; naming the group alone must still fill the box rather than leave it empty.
            const data = f.options ?? (f.enumGroup ? enumOptions(f.enumGroup) : []);
            return (
              <Select
                key={f.name}
                {...common}
                {...form.getInputProps(f.name)}
                data={data}
                placeholder={f.placeholder ?? 'Choose one'}
                comboboxProps={{ withinPortal: true }}
                clearable={!f.required}
              />
            );
          }
          case 'entity': {
            const resource = typeof f.resource === 'function' ? f.resource(values) : f.resource;
            if (!resource) {
              return (
                <TextInput
                  key={f.name}
                  {...common}
                  {...form.getInputProps(f.name)}
                  description={f.description ?? 'Choose the type above first'}
                  disabled
                />
              );
            }
            return (
              <EntitySelect
                key={f.name}
                entity={resource}
                label={f.label}
                description={f.description}
                required={f.required}
                error={form.errors[f.name]}
                value={(values[f.name] as string) ?? null}
                onChange={(v) => form.setFieldValue(f.name, v ?? '')}
              />
            );
          }
          case 'date':
            return (
              <DateInput
                key={f.name}
                {...common}
                value={(values[f.name] as string | null) ?? null}
                onChange={(v) => form.setFieldValue(f.name, v ?? '')}
                valueFormat="D MMM YYYY"
                placeholder={f.placeholder ?? 'Pick a date'}
                clearable={!f.required}
                popoverProps={{ withinPortal: true }}
              />
            );
          case 'datetime':
            return (
              <DateTimePicker
                key={f.name}
                {...common}
                value={(values[f.name] as string | null) ?? null}
                onChange={(v) => form.setFieldValue(f.name, v ?? '')}
                valueFormat="D MMM YYYY, h:mm A"
                placeholder={f.placeholder ?? 'Pick a date and time'}
                clearable={!f.required}
                popoverProps={{ withinPortal: true }}
              />
            );
          case 'rows': {
            const rowFields = f.rowFields ?? [];
            const rows = Array.isArray(values[f.name]) ? (values[f.name] as Record<string, unknown>[]) : [];
            const setRows = (next: Record<string, unknown>[]) => form.setFieldValue(f.name, next);
            return (
              <div key={f.name}>
                <Text size="sm" fw={500}>
                  {f.label}
                  {f.required && <span style={{ color: 'var(--mantine-color-error)' }}> *</span>}
                </Text>
                {f.description && (
                  <Text size="xs" c="dimmed" mb={6}>
                    {f.description}
                  </Text>
                )}
                <Stack gap="xs">
                  {rows.map((row, index) => (
                    // The rule is right and this is a known gap, not a false positive. The comment
                    // that stood here said rows are "never reordered" — but Remove below is
                    // `filter((_, i) => i !== index)`, and deleting a row from the middle shifts
                    // every row after it down an index, so React reuses the deleted row's DOM for
                    // its successor. The values are controlled from form state and redraw
                    // correctly; focus and a number input's half-typed text do not. A stable id per
                    // row would have to survive the immutable `rows.map(...)` update on every
                    // keystroke without leaking into the submitted payload — a change to how this
                    // shared component holds state, not something to slip into a commit whose
                    // purpose is that nothing behaves differently. Recorded in
                    // docs/track-1-status.md.
                    // biome-ignore lint/suspicious/noArrayIndexKey: known gap, reasoned above
                    <Paper key={`${f.name}-${index}`} withBorder p="xs" radius="sm">
                      <Group gap="xs" align="flex-end" wrap="wrap">
                        {rowFields.map((sub) => {
                          const setSub = (v: unknown) =>
                            setRows(rows.map((r, i) => (i === index ? { ...r, [sub.name]: v } : r)));
                          const unit = resolveUnit(sub.unit, row);
                          const common = {
                            label: sub.label,
                            description: sub.description,
                            size: 'sm' as const,
                          };
                          if (sub.type === 'number') {
                            return (
                              <NumberInput
                                key={sub.name}
                                {...common}
                                value={(row[sub.name] as number | '') ?? ''}
                                onChange={setSub}
                                decimalScale={UNIT_DECIMALS[unit]}
                                suffix={unit === 'percent' ? '%' : undefined}
                                thousandSeparator={unit === 'money' ? ',' : undefined}
                                style={{ width: 150 }}
                              />
                            );
                          }
                          if (sub.type === 'select') {
                            return (
                              <Select
                                key={sub.name}
                                {...common}
                                data={sub.options ?? (sub.enumGroup ? enumOptions(sub.enumGroup) : [])}
                                value={(row[sub.name] as string) ?? null}
                                onChange={(v) => setSub(v)}
                                comboboxProps={{ withinPortal: true }}
                                style={{ width: 170 }}
                              />
                            );
                          }
                          if (sub.type === 'boolean') {
                            return (
                              <Checkbox
                                key={sub.name}
                                label={sub.label}
                                checked={Boolean(row[sub.name])}
                                onChange={(e) => setSub(e.currentTarget.checked)}
                              />
                            );
                          }
                          return (
                            <TextInput
                              key={sub.name}
                              {...common}
                              value={String(row[sub.name] ?? '')}
                              onChange={(e) => setSub(e.currentTarget.value)}
                              placeholder={sub.placeholder}
                              style={{ width: 170 }}
                            />
                          );
                        })}
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          color="red"
                          onClick={() => setRows(rows.filter((_, i) => i !== index))}
                        >
                          Remove
                        </Button>
                      </Group>
                    </Paper>
                  ))}
                  <Button
                    size="compact-sm"
                    variant="light"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() =>
                      setRows([
                        ...rows,
                        Object.fromEntries(rowFields.map((sub) => [sub.name, sub.defaultValue ?? ''])),
                      ])
                    }
                  >
                    Add {f.rowNoun ? withArticle(f.rowNoun) : 'another'}
                  </Button>
                </Stack>
                {form.errors[f.name] && (
                  <Text size="xs" c="red" mt={4}>
                    {form.errors[f.name]}
                  </Text>
                )}
              </div>
            );
          }
          case 'entities': {
            const resource = typeof f.resource === 'function' ? f.resource(values) : f.resource;
            if (!resource) return null;
            const picked = Array.isArray(values[f.name]) ? (values[f.name] as string[]) : [];
            return (
              <EntityMultiSelect
                key={f.name}
                entity={resource}
                label={f.label}
                description={f.description}
                required={f.required}
                error={form.errors[f.name]}
                value={picked}
                onChange={(v) => form.setFieldValue(f.name, v)}
              />
            );
          }
          case 'weekdays':
            return (
              <WeekdaysField
                key={f.name}
                label={f.label}
                description={f.description}
                value={values[f.name]}
                onChange={(days) => form.setFieldValue(f.name, days)}
                emptyMeans={f.placeholder}
                disabled={frozen}
              />
            );
          case 'schedule':
            return (
              <ScheduleField
                key={f.name}
                label={f.label}
                description={f.description}
                value={values[f.name]}
                onChange={(v) => form.setFieldValue(f.name, v)}
                emptyMeans={f.placeholder}
              />
            );
          case 'eligibility':
            return (
              <EligibilityField
                key={f.name}
                label={f.label}
                description={f.description}
                value={values[f.name]}
                onChange={(v) => form.setFieldValue(f.name, v)}
              />
            );
          case 'json':
            return (
              <JsonInput
                key={f.name}
                {...common}
                {...form.getInputProps(f.name)}
                autosize
                minRows={3}
                formatOnBlur
                validationError="This is not valid JSON yet"
                placeholder={f.placeholder}
              />
            );
          default:
            return f.suggest ? (
              <SuggestingTextInput
                key={f.name}
                {...common}
                placeholder={f.placeholder}
                form={form}
                name={f.name}
                suggestion={f.suggest(values)}
              />
            ) : (
              <TextInput
                key={f.name}
                {...common}
                {...form.getInputProps(f.name)}
                placeholder={f.placeholder}
              />
            );
        }
      })}
    </Stack>
  );
}

/**
 * A box that keeps up with a suggestion until somebody types in it.
 *
 * The one field this exists for is "More than one", beside the unit a stock item is counted in.
 * Left as a placeholder the suggestion would be grey text that never got saved, and the plural
 * would go on being unanswered for every word the owner did not stop and think about; applied
 * silently it would be the guessing rule this whole design refuses. So it is a real value, in a
 * real box, that follows what he types in "Counted in" and stops following the instant he touches
 * it — including when he clears it, which is how "leave it alone" is said.
 */
function SuggestingTextInput({
  form,
  name,
  suggestion,
  ...rest
}: {
  form: CrudForm;
  name: string;
  suggestion: string;
} & Omit<TextInputProps, 'form' | 'name' | 'value' | 'onChange'>) {
  const [typed, setTyped] = useState(false);
  const value = String(form.getValues()[name] ?? '');

  useEffect(() => {
    if (typed || value === suggestion) return;
    form.setFieldValue(name, suggestion);
  }, [typed, value, suggestion, name, form]);

  return (
    <TextInput
      {...rest}
      value={value}
      onChange={(event) => {
        setTyped(true);
        form.setFieldValue(name, event.currentTarget.value);
      }}
    />
  );
}

/**
 * Field-level problems, before anything is sent.
 *
 * `required: true` used to be decorative — nothing validated it, so the server was the first thing
 * to notice, and it said so in a red corner toast.
 */
export function validateFields(
  fields: FieldDef[],
  values: Record<string, unknown>,
  editing: boolean,
): Record<string, string> {
  return Object.fromEntries(
    fields
      .map((f) => {
        // A box that is greyed out on an edit, and whose value this form never sends, must not
        // be able to refuse the save. Tighten a rule — two digits, hyphens only — and every row
        // saved before it existed would otherwise be frozen for good: the error lands on a box
        // nobody can type in, so a reason code typed as "Broken Glass" last month could never
        // even be retired. The rule still bites where it can be obeyed, which is on the way in.
        if (editing && f.fixedAfterCreate) return null;
        const v = values[f.name];
        if (f.type === 'rows' && f.required && (!Array.isArray(v) || v.length === 0)) {
          return [f.name, `Add at least one ${f.rowNoun ?? 'entry'}`];
        }
        if (f.required && (v === '' || v === null || v === undefined)) {
          return [f.name, `${f.label} is required`];
        }
        if (f.type === 'phone' && v && !isValidInternationalPhone(v)) {
          return [f.name, 'Choose a country and enter a complete phone number'];
        }
        const custom = f.validate?.(v, values);
        return custom ? [f.name, custom] : null;
      })
      .filter((e): e is [string, string] => e !== null),
  );
}

/** A stored row → the values the form edits: money out of minor units, rates out of basis points. */
export function seedFormValues(fields: FieldDef[], row: Record<string, unknown>) {
  return Object.fromEntries(
    fields.map((f) => {
      const v = row[f.name];
      if (f.type === 'number') return [f.name, toDisplayNumber(v, resolveUnit(f.unit, row))];
      if (f.type === 'rows') return [f.name, rowsToDisplay(v, f.rowFields ?? [])];
      if (f.type === 'entities' || f.type === 'eligibility') return [f.name, Array.isArray(v) ? v : []];
      if (f.type === 'schedule') return [f.name, v ?? null];
      if (f.type === 'json') return [f.name, v == null ? '' : JSON.stringify(v, null, 2)];
      if (f.type === 'date' || f.type === 'datetime') return [f.name, fromIsoDate(v as string) ?? ''];
      return [f.name, v ?? (f.type === 'boolean' ? false : '')];
    }),
  );
}

export function buildBody(
  values: Record<string, unknown>,
  fields: FieldDef[],
  isEdit: boolean,
  defaults?: Record<string, unknown>,
) {
  const body: Record<string, unknown> = isEdit ? {} : { ...defaults };
  for (const f of fields) {
    // Left out entirely rather than sent and ignored: an update endpoint that drops a key it does
    // not accept would otherwise answer 200, and the toast would report a save that did nothing.
    if (isEdit && f.fixedAfterCreate) continue;
    const v = values[f.name];
    // An empty list is empty the same way a blank box is: a nullable column takes an explicit null
    // on edit, and on create the column default applies. Without this, clearing every entry sends
    // `[]` where the column expects null — which reads back as "some list", not "no list".
    const isEmpty =
      v === '' ||
      v === undefined ||
      v === null ||
      ((f.type === 'entities' || f.type === 'rows') && Array.isArray(v) && v.length === 0 && f.nullable);

    if (isEmpty) {
      // On create, an untouched field is simply absent so the column default applies. On edit,
      // clearing a nullable field has to travel as an explicit null — otherwise the request omits
      // it, the server keeps the old value, and the user gets a green "Saved" over a change that
      // never happened.
      if (isEdit && f.nullable) body[f.name] = null;
      continue;
    }

    if (f.type === 'number') {
      const stored = toStoredNumber(v, resolveUnit(f.unit, values));
      if (stored !== undefined) body[f.name] = stored;
      continue;
    }
    if (f.type === 'date') {
      body[f.name] = v instanceof Date ? toIsoDate(v) : v;
      continue;
    }
    if (f.type === 'datetime') {
      body[f.name] = v instanceof Date ? v.toISOString() : v;
      continue;
    }
    if (f.type === 'rows') {
      body[f.name] = rowsToStored(v, f.rowFields ?? []);
      continue;
    }
    if (f.type === 'json' && typeof v === 'string') {
      try {
        body[f.name] = JSON.parse(v);
      } catch {
        throw new Error(`${f.label} is not valid JSON`);
      }
      continue;
    }
    body[f.name] = v;
  }
  return body;
}

/** Cell content for a column, given the shared formatting rules. */
function renderCell(col: ColumnDef, row: Record<string, unknown>): ReactNode {
  if (col.render) return col.render(row);
  const raw = row[col.accessor];

  if (col.reference) return <EntityLabel entity={col.reference} id={raw} />;

  if (col.enumGroup) {
    const label = enumLabel(col.enumGroup, raw);
    const color = raw == null ? undefined : enumColor(col.enumGroup, raw);
    return color ? (
      <Badge color={color} variant="light">
        {label}
      </Badge>
    ) : (
      label
    );
  }

  switch (col.unit) {
    case 'money': {
      const currency = col.currencyAccessor ? row[col.currencyAccessor] : row.currency;
      return formatMoney(raw as number, (currency as string) ?? 'HNL');
    }
    case 'percent':
      return formatRateBp(raw as number);
    case 'date':
      return formatDate(raw as string);
    case 'datetime':
      return formatDateTime(raw as string);
    default:
      break;
  }

  if (raw === null || raw === undefined || raw === '') return <Text c="dimmed">—</Text>;
  if (typeof raw === 'boolean') return raw ? 'Yes' : '';
  if (typeof raw === 'object') return <Text size="xs">{JSON.stringify(raw)}</Text>;
  return String(raw);
}

export function CrudSection(props: CrudSectionProps) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  /** Set when a save landed in a month the accountant has already filed. See the dialog for why. */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const noun = props.entityNoun ?? 'record';
  const fields = useMemo(() => props.fields ?? [], [props.fields]);

  const form = useForm<Record<string, unknown>>({
    initialValues: {},
    validate: (values) => validateFields(fields, values, Boolean(editing)),
  });

  const queryString = props.listQuery
    ? Object.entries(props.listQuery)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  const requestPath = queryString ? `${props.listPath}?${queryString}` : props.listPath;

  const query = useQuery({
    queryKey: listKey(props.listPath, props.listQuery),
    queryFn: () => rawApi<unknown>('GET', requestPath),
    enabled: !props.formOnly && (props.listEnabled ?? true),
    placeholderData: (previous) => previous,
  });

  const rows = props.extractList
    ? props.extractList(query.data ?? [])
    : ((query.data ?? []) as Record<string, unknown>[]);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: listKey(props.listPath, props.listQuery) });

  const describeRow = (row: Record<string, unknown> | null) =>
    row ? (props.rowLabel?.(row) ?? String(row.name ?? row.displayName ?? row.code ?? noun)) : noun;

  const save = useMutation({
    mutationFn: async (input: { values: Record<string, unknown>; filedMonthReason?: string }) => {
      const body = buildBody(input.values, fields, Boolean(editing), props.defaults);
      // Added only after the server has asked for it. Sending it on every write would put a box on
      // the ninety-nine saves out of a hundred that land in a month nobody has filed, which is the
      // fastest way to teach somebody to type "n/a" into the one that matters.
      if (input.filedMonthReason) body[FILED_MONTH_REASON_FIELD] = input.filedMonthReason;
      if (editing && props.updatePathTemplate) {
        return rawApi('PATCH', props.updatePathTemplate.replace('{id}', String(editing.id)), body);
      }
      return rawApi('POST', props.createPath ?? props.listPath, body);
    },
    onSuccess: (data, input) => {
      const wasEditing = Boolean(editing);
      const label = editing ? describeRow(editing) : describeRow(input.values);
      setFiledMonthWarning(null);
      closeModal();
      refresh();
      notifySaved(wasEditing ? `Updated ${label}` : `Added ${label}`);
      /*
       * A door may answer 200 AND have something to say. The pricing write door does: a lempira
       * amount beside a dollar one that implies an exchange rate far from the filed one is accepted
       * — it has to be, the owner's own card prints "$2 / 50 Lempira" — and answered with a
       * question. Read here rather than in each screen, because every screen saves through this one
       * component and the question is the same shape wherever it comes from.
       */
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const warnings = (data as { warnings?: unknown }).warnings;
        if (Array.isArray(warnings) && warnings.length > 0) {
          notifyWarnings(warnings as { message: string }[]);
        }
      }
      if (!wasEditing && data && typeof data === 'object' && !Array.isArray(data)) {
        props.onCreated?.(data as Record<string, unknown>);
      }
    },
    onError: (err: Error) => {
      /*
       * A month the accountant has already filed is a QUESTION, not a refusal — the entry is
       * usually a real piece of paper that arrived late, and the office's job is to warn and then
       * get out of the way. Asked here, once, for every screen this component draws, rather than in
       * each of them: a red toast would leave the manager with nothing to do but give up.
       */
      if (isFiledMonthWarning(err)) {
        setFiledMonthWarning(err);
        return;
      }
      // Field-level problems belong on the fields; only the summary goes to the toast.
      const byField = fieldErrors(err);
      if (Object.keys(byField).length) form.setErrors(byField);
      notifyError(err, `save this ${noun}`);
    },
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.reset();
  };

  const openCreate = () => {
    setEditing(null);
    // A checkbox seeded to false regardless of the column's real default is a quiet trap: the
    // database says `active` defaults to true, the untouched form says false, and the manager who
    // adds "Cocktails" without noticing the box gets a category nobody can see.
    form.setValues(
      Object.fromEntries(
        fields.map((f) => [
          f.name,
          f.defaultValue ??
            (f.type === 'boolean'
              ? false
              : f.type === 'rows' || f.type === 'entities' || f.type === 'eligibility'
                ? []
                : // A new schedule is null, not `{rules: []}` — "always" rather than "a schedule
                  // that never matches", which are opposites.
                  f.type === 'schedule'
                  ? null
                  : ''),
        ]),
      ),
    );
    form.resetDirty();
    setModalOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    if (!props.updatePathTemplate || !fields.length) return;
    setEditing(row);
    const seeded = seedFormValues(fields, row);
    form.setValues(seeded);
    form.resetDirty(seeded);
    setModalOpen(true);
  };

  const canEdit = Boolean(props.updatePathTemplate && fields.length);

  const columns = [
    ...props.columns.map((c) => ({
      accessor: c.accessor,
      title: c.title,
      noWrap: c.noWrap,
      sortable: c.sortable,
      render: (row: Record<string, unknown>) => renderCell(c, row),
    })),
    ...(canEdit || props.rowActions
      ? [
          {
            accessor: '__actions',
            title: '',
            textAlign: 'right' as const,
            noWrap: true,
            render: (row: Record<string, unknown>) => (
              <Group gap="xs" justify="flex-end" wrap="nowrap">
                {props.rowActions?.(row, refresh)}
                {canEdit && (
                  <Button size="xs" variant="light" onClick={() => openEdit(row)}>
                    Edit
                  </Button>
                )}
              </Group>
            ),
          },
        ]
      : []),
  ];
  const paginationProps: DataTablePaginationProps = props.pagination
    ? {
        page: props.pagination.page,
        recordsPerPage: props.pagination.recordsPerPage,
        totalRecords: props.pagination.totalRecords(query.data),
        onPageChange: props.pagination.onPageChange,
      }
    : {};
  const sortProps: DataTableSortProps<Record<string, unknown>> =
    props.sortStatus && props.onSortStatusChange
      ? { sortStatus: props.sortStatus, onSortStatusChange: props.onSortStatusChange }
      : {};

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      {/*
        Wraps rather than squeezes. This header used to be `wrap="nowrap"`, which on a section
        carrying several controls — On hand has an area picker and three buttons — left the
        description a column about thirty pixels wide and set it one word per line all the way down
        the screen. It did that at 1024, which is a laptop and an iPad in landscape, not an edge
        case. The buttons keep their own nowrap so they never stack into a ladder; it is the row
        holding the two halves that is allowed to break, and `flex: 1 1 320px` is what makes the
        text side ask for a readable width before it gives any of it up.
      */}
      <Group justify="space-between" mb="sm" wrap="wrap" align="flex-start" gap="sm">
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <Group gap="xs">
            <Title order={5}>{props.title}</Title>
            {query.isFetching && !query.isLoading && <Loader size={14} />}
          </Group>
          {props.description && (
            <Text size="sm" c="dimmed" mt={2}>
              {props.description}
            </Text>
          )}
        </div>
        <Group gap="xs" wrap="nowrap">
          {props.actions}
          {fields.length > 0 && (
            // Held until every default has resolved. Most sections pass `defaults={{ locationId }}`
            // off a query that is still in flight for the first moment of the page, and a create
            // submitted in that moment posts `locationId: undefined` — dropped by the serializer,
            // 400 from the server, on a form the person filled in correctly.
            <Button
              size="sm"
              onClick={openCreate}
              disabled={Object.values(props.defaults ?? {}).some((v) => v === undefined)}
            >
              Add {props.entityNoun ? withArticle(props.entityNoun) : 'new'}
            </Button>
          )}
        </Group>
      </Group>

      {props.tableControls && (
        <div style={{ marginBottom: 'var(--mantine-spacing-md)' }}>{props.tableControls}</div>
      )}

      {/* An outage used to be indistinguishable from an empty collection: both rendered
          "Nothing here yet", so a manager would conclude their data was gone. */}
      {query.isError && !props.formOnly ? (
        <Alert color="red" title="Could not load this list">
          <Text size="sm" mb="sm">
            The information could not be reached. Nothing has been lost — this is only a display problem.
          </Text>
          <Button size="xs" variant="light" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : props.formOnly ? null : (
        // Scroll sideways rather than crush. On a phone behind the bar the columns were squeezing
        // to the point that names wrapped to five lines and badges truncated to two characters —
        // a table that technically rendered and could not be read.
        <ScrollArea type="auto" offsetScrollbars>
          <DataTable
            withTableBorder={false}
            idAccessor="id"
            records={rows}
            columns={columns}
            {...paginationProps}
            {...sortProps}
            rowBackgroundColor={
              props.rowBackgroundColor
                ? (row: Record<string, unknown>) => props.rowBackgroundColor?.(row)
                : undefined
            }
            fetching={query.isLoading}
            minHeight={rows.length === 0 ? 140 : undefined}
            noRecordsText={props.emptyMessage ?? `No ${props.title.toLowerCase()} yet`}
            highlightOnHover
            verticalSpacing="sm"
            striped
            styles={{ table: { minWidth: Math.max(560, columns.length * 150) } }}
          />
        </ScrollArea>
      )}

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={
          editing
            ? `Edit ${describeRow(editing)}`
            : `Add ${props.entityNoun ? withArticle(props.entityNoun) : 'record'}`
        }
        size="lg"
        // Half-completed forms are lost to a stray click otherwise.
        closeOnClickOutside={false}
      >
        <form
          onSubmit={form.onSubmit((values) => {
            save.mutate({ values });
          })}
        >
          <FormFields form={form} fields={fields} editing={Boolean(editing)} />
          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={closeModal} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {editing ? 'Save changes' : `Add ${props.entityNoun ?? 'record'}`}
            </Button>
          </Group>
        </form>
      </Modal>

      {/* Sits over the form, which stays open behind it: cancelling here returns him to what he
          typed rather than throwing it away. */}
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(reason) => save.mutate({ values: form.values, filedMonthReason: reason })}
      />
    </Paper>
  );
}
